import React from 'react';
import { DispatchProp } from 'react-redux';

import { TabList, TabListItem, InformationCollapsible } from '@dndbeyond/character-common-components/es';
import {
    HtmlContent,
    DarkChevronUpSvg,
    DdbBadgeSvg,
    DisabledChevronDownSvg,
    RacialTraitsShortDescription,
} from '@dndbeyond/character-components/es';
import {
    ApiAdapterPromise,
    ApiAdapterRequestConfig,
    ApiResponse,
    characterActions,
    CharacterPreferences,
    ChoiceData,
    Constants,
    FeatLookup,
    FeatDefinitionContract,
    PrerequisiteData,
    Race,
    RaceUtils,
    RacialTraitUtils,
    RuleData,
    RuleDataUtils,
    rulesEngineSelectors,
    EntitledEntity,
    RacialTraitDefinitionContract,
    serviceDataActions,
    serviceDataSelectors,
    DefinitionPool,
    DefinitionUtils,
    OptionalOriginLookup,
    HelperUtils,
    OptionalOriginUtils,
    ClassSpellListSpellsLookup,
} from '@dndbeyond/character-rules-engine/es';

import { confirmModalActions } from '../../../../Shared/actions/confirmModal';
import { SimpleClassSpellList } from '../../../../Shared/components/SimpleClassSpellList';
import { apiCreatorSelectors } from '../../../../Shared/selectors';
import Page from '../../../components/Page';
import PageBody from '../../../components/PageBody';
import PageHeader from '../../../components/PageHeader';
import RacialTraitList from '../../../components/RacialTraitList';
import { navigationConfig } from '../../../config';
import { RouteKey } from '../../../constants/navigation';
import ConnectedBuilderPage from '../ConnectedBuilderPage';
import { OptionalOriginManager } from './OptionalOriginManager';
import { NavigateFunction, useNavigate } from 'react-router-dom';

interface Props extends DispatchProp {
    ruleData: RuleData;
    preferences: CharacterPreferences;
    prerequisiteData: PrerequisiteData;
    choiceInfo: ChoiceData;
    race: Race | null;
    featLookup: FeatLookup;
    definitionPool: DefinitionPool;
    optionalOriginLookup: OptionalOriginLookup;
    classSpellListSpellsLookup: ClassSpellListSpellsLookup;
    navigate: NavigateFunction;
    characterId: number;
    loadAvailableOptionalRacialTraits: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<EntitledEntity<RacialTraitDefinitionContract>>>;
    loadAvailableFeats: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<FeatDefinitionContract>>>;
}
class RaceManage extends React.PureComponent<Props> {
    handleChangeRace = (): void => {
        const { navigate, characterId } = this.props;
        navigate(navigationConfig.getRouteDefPath(RouteKey.RACE_CHOOSE).replace(":characterId", characterId))
    };

    handleRacialTraitChoiceChange = (racialTraitId, choiceId, choiceType, optionValue): void => {
        const { dispatch } = this.props;

        dispatch(characterActions.racialTraitChoiceSetRequest(racialTraitId, choiceType, choiceId, optionValue));
    };

    handleDefinitionsLoaded = (entitledData: EntitledEntity<RacialTraitDefinitionContract>): void => {
        const { dispatch } = this.props;

        dispatch(serviceDataActions.definitionPoolAdd(entitledData.definitionData, entitledData.accessTypes));
    };

    handleOptionalOriginSelection = (definitionKey: string, affectedRacialTraitDefinitionKey: string | null): void => {
        const { dispatch } = this.props;

        const racialTraitId = DefinitionUtils.hack__getDefinitionKeyId(definitionKey);
        if (racialTraitId === null) {
            return;
        }

        const affectedRacialTraitId = affectedRacialTraitDefinitionKey
            ? DefinitionUtils.hack__getDefinitionKeyId(affectedRacialTraitDefinitionKey)
            : null;

        dispatch(characterActions.optionalOriginCreate(racialTraitId, affectedRacialTraitId));
    };

    handleRemoveOptionalOriginSelectionPromise = (
        definitionKey: string,
        newIsEnabled: boolean,
        accept: () => void,
        reject: () => void,
    ): void => {
        const { dispatch, optionalOriginLookup, classSpellListSpellsLookup } = this.props;

        const optionalOrigin = HelperUtils.lookupDataOrFallback(optionalOriginLookup, definitionKey);
        if (optionalOrigin === null) {
            return;
        }

        const optionalRacialTrait = OptionalOriginUtils.getRacialTrait(optionalOrigin);
        if (optionalRacialTrait === null) {
            return;
        }

        const racialTraitId = OptionalOriginUtils.getRacialTraitId(optionalOrigin);
        const spellListIds = OptionalOriginUtils.getRemoveMappingSpellListIds(optionalOrigin);
        const hasSpellsToRemove = spellListIds.some((id) => classSpellListSpellsLookup.hasOwnProperty(id));

        if (!hasSpellsToRemove) {
            dispatch(characterActions.optionalOriginDestroy(racialTraitId));
            accept();
        } else {
            dispatch(
                confirmModalActions.create({
                    conClsNames: ['confirm-modal-remove'],
                    heading: 'Customized Origin Warning',
                    acceptBtnClsNames: ['character-button-remove'],
                    content: (
                        <div className="">
                            <p>
                                You are about to remove <strong>{RacialTraitUtils.getName(optionalRacialTrait)}</strong>{' '}
                                from your character.
                            </p>
                            <p>
                                After doing so, the following spells provided by this feature will be removed from your
                                character:
                            </p>
                            <SimpleClassSpellList
                                spellListIds={spellListIds}
                                classSpellListSpellsLookup={classSpellListSpellsLookup}
                            />
                        </div>
                    ),
                    onConfirm: () => {
                        dispatch(characterActions.optionalOriginDestroy(racialTraitId));
                        accept();
                    },
                    onCancel: () => {
                        reject();
                    },
                }),
            );
        }
    };

    handleOnChangeOptionalOriginReplacementPromise = (
        definitionKey: string,
        newAffectedDefinitionKey: string | null,
        oldAffectedDefinitionKey: string | null,
        accept: () => void,
        reject: () => void,
    ): void => {
        const { dispatch, optionalOriginLookup, classSpellListSpellsLookup } = this.props;

        if (newAffectedDefinitionKey === oldAffectedDefinitionKey) {
            return;
        }

        const optionalOrigin = HelperUtils.lookupDataOrFallback(optionalOriginLookup, definitionKey);
        if (optionalOrigin === null) {
            return;
        }

        const optionalRacialTrait = OptionalOriginUtils.getRacialTrait(optionalOrigin);
        if (optionalRacialTrait === null) {
            return;
        }

        const racialTraitId = OptionalOriginUtils.getRacialTraitId(optionalOrigin);
        const newAffectedRacialTraitId: number | null = newAffectedDefinitionKey
            ? DefinitionUtils.hack__getDefinitionKeyId(newAffectedDefinitionKey)
            : null;

        const spellListIds = OptionalOriginUtils.getUpdateMappingSpellListIdsToRemove(optionalOrigin, {
            affectedRacialTraitId: newAffectedRacialTraitId,
        });
        const hasSpellsToRemove = spellListIds.some((id) => classSpellListSpellsLookup.hasOwnProperty(id));

        if (!hasSpellsToRemove) {
            dispatch(characterActions.optionalOriginSetRequest(racialTraitId, newAffectedRacialTraitId));
            accept();
        } else {
            dispatch(
                confirmModalActions.create({
                    conClsNames: ['confirm-modal-remove'],
                    acceptBtnClsNames: ['character-button-remove'],
                    heading: 'Customized Origin Warning',
                    content: (
                        <div className="">
                            <p>
                                You are about to change the Racial Trait to be replaced by{' '}
                                <strong>{RacialTraitUtils.getName(optionalRacialTrait)}</strong>
                            </p>
                            <p>
                                After doing so, the following spells provided by this feature will be removed from your
                                character:
                            </p>
                            <SimpleClassSpellList
                                spellListIds={spellListIds}
                                classSpellListSpellsLookup={classSpellListSpellsLookup}
                            />
                        </div>
                    ),
                    onConfirm: () => {
                        dispatch(characterActions.optionalOriginSetRequest(racialTraitId, newAffectedRacialTraitId));
                        accept();
                    },
                    onCancel: () => {
                        reject();
                    },
                }),
            );
        }
    };

    renderRacialTraitsGroup = (): React.ReactNode => {
        const {
            race,
            loadAvailableFeats,
            choiceInfo,
            prerequisiteData,
            preferences,
            featLookup,
            optionalOriginLookup,
            definitionPool,
        } = this.props;

        if (!race) {
            return null;
        }

        const racialTraits = RaceUtils.getVisibleRacialTraits(race);
        const filteredRacialTraits = RacialTraitUtils.filterRacialTraitsByDisplayConfigurationType(
            racialTraits,
            Constants.DisplayConfigurationTypeEnum.RACIAL_TRAIT,
        );

        return (
            <div className="race-detail-secondary">
                {filteredRacialTraits.length > 0 && (
                    <div className="race-detail-racial-traits">
                        <RacialTraitList
                            racialTraits={filteredRacialTraits}
                            featLookup={featLookup}
                            loadAvailableFeats={loadAvailableFeats}
                            handleRacialTraitChoiceChange={this.handleRacialTraitChoiceChange}
                            choiceInfo={choiceInfo}
                            prerequisiteData={prerequisiteData}
                            preferences={preferences}
                            optionalOriginLookup={optionalOriginLookup}
                            definitionPool={definitionPool}
                        />
                    </div>
                )}
            </div>
        );
    };

    renderRacialTraitsContent = () => {
        const { preferences } = this.props;

        if (preferences.enableOptionalOrigins) {
            return this.renderTabList();
        }

        return this.renderRacialTraitsGroup();
    };

    renderTabList = () => {
        const { race, optionalOriginLookup, loadAvailableOptionalRacialTraits, definitionPool, ruleData } = this.props;

        if (!race) {
            return null;
        }

        return (
            <TabList
                initialActiveKey="traits"
                maxNavItemsShow={5}
                className="manage-race-tabs"
                clickActiveTabToHide={true}
            >
                <TabListItem
                    tkey="traits"
                    label="Racial Traits"
                    inactiveNavCalloutNode={<DisabledChevronDownSvg />}
                    activeNavCalloutNode={<DarkChevronUpSvg />}
                >
                    {this.renderRacialTraitsGroup()}
                </TabListItem>
                <TabListItem
                    tkey="optional-origins"
                    label="Origin Manager"
                    inactiveNavCalloutNode={<DisabledChevronDownSvg />}
                    activeNavCalloutNode={<DarkChevronUpSvg />}
                >
                    <OptionalOriginManager
                        race={race}
                        definitionPool={definitionPool}
                        optionalOriginLookup={optionalOriginLookup}
                        onDefinitionsLoaded={this.handleDefinitionsLoaded}
                        loadAvailableOptionalRacialTraits={loadAvailableOptionalRacialTraits}
                        onSelection={this.handleOptionalOriginSelection}
                        onChangeReplacementPromise={this.handleOnChangeOptionalOriginReplacementPromise}
                        onRemoveSelectionPromise={this.handleRemoveOptionalOriginSelectionPromise}
                        ruleData={ruleData}
                    />
                </TabListItem>
            </TabList>
        );
    };

    renderInformationCollapsible = (): React.ReactNode => {
        const { ruleData, race } = this.props;
        if (race === null) {
            return null;
        }

        const definitionKey = DefinitionUtils.hack__generateDefinitionKey(
            RaceUtils.getEntityRaceTypeId(race),
            RaceUtils.getEntityRaceId(race),
        );
        const builderText = RuleDataUtils.getBuilderHelperTextByDefinitionKey(
            definitionKey,
            ruleData,
            Constants.DisplayConfigurationTypeEnum.RACIAL_TRAIT,
        );

        return builderText.map((helperText, idx) => (
            <InformationCollapsible
                key={idx}
                title={helperText.label}
                description={helperText.description}
                headerIcon={<DdbBadgeSvg />}
                collapsibleIcon={<DisabledChevronDownSvg />}
            />
        ));
    };

    render() {
        const { race, ruleData } = this.props;

        //TODO render something else if race doesnt exist
        if (!race) {
            return null;
        }

        const fullName = RaceUtils.getFullName(race);
        const portraitAvatarUrl = RaceUtils.getPortraitAvatarUrl(race);
        const description = RaceUtils.getDescription(race);
        const moreDetailsUrl = RaceUtils.getMoreDetailsUrl(race);
        const previewUrl: string | null = portraitAvatarUrl ?? RuleDataUtils.getDefaultRaceImageUrl(ruleData);

        const descriptionClassNames: Array<string> = ['race-detail-description'];

        const calledOutRacialTraits = RaceUtils.getCalledOutRacialTraits(race);
        if (calledOutRacialTraits.length > 0) {
            descriptionClassNames.push('race-detail-description--hide-traits');
        }

        return (
            <Page>
                <PageBody>
                    <div className="manage-race race-detail">
                        <div className="race-detail-primary">
                            <div className="race-detail-aside">
                                <div className="race-detail-preview">
                                    <img
                                        className="race-detail-preview-img"
                                        src={previewUrl ? previewUrl : ''}
                                        alt=""
                                    />
                                </div>
                                <div className="manage-race-chosen-action" onClick={this.handleChangeRace}>
                                    Change Race
                                </div>
                            </div>
                            <PageHeader>{fullName}</PageHeader>
                            <HtmlContent
                                className={descriptionClassNames.join(' ')}
                                html={description ? description : ''}
                            />
                            {calledOutRacialTraits.length > 0 && (
                                <RacialTraitsShortDescription calledOutRacialTraits={calledOutRacialTraits} />
                            )}
                            <div className="race-detail-more">
                                <a className="race-detail-more-link" href={moreDetailsUrl ? moreDetailsUrl : ''}>
                                    {fullName} Details Page
                                </a>
                            </div>
                            {this.renderInformationCollapsible()}
                        </div>
                        {this.renderRacialTraitsContent()}
                    </div>
                </PageBody>
            </Page>
        );
    }
}

const RaceManageWithHooks = (props) => {
    const navigate = useNavigate();
    return <RaceManage {...props} navigate={navigate}/>
}

export default ConnectedBuilderPage(RaceManageWithHooks, RouteKey.RACE_MANAGE, (state) => {
    return {
        characterId: rulesEngineSelectors.getId(state),
        race: rulesEngineSelectors.getRace(state),
        choiceInfo: rulesEngineSelectors.getChoiceInfo(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        prerequisiteData: rulesEngineSelectors.getPrerequisiteData(state),
        preferences: rulesEngineSelectors.getCharacterPreferences(state),
        featLookup: rulesEngineSelectors.getFeatLookup(state),
        definitionPool: serviceDataSelectors.getDefinitionPool(state),
        loadAvailableOptionalRacialTraits: apiCreatorSelectors.makeLoadAvailableOptionalRacialTraits(state),
        loadAvailableFeats: apiCreatorSelectors.makeLoadAvailableFeats(state),
        optionalOriginLookup: rulesEngineSelectors.getOptionalOriginLookup(state),
        classSpellListSpellsLookup: rulesEngineSelectors.getClassSpellListSpellsLookup(state),
    };
});
